import {fdelete, fget, fpatch, fpost, fput, fpost_anonymous} from "./ffetch";

export function verifyToken(token) {
    return fpost_anonymous('/token/verify/', {token: token})
        .then(async (response) => {
            return {
                status_code: response.status,
                response_data: await response.json(),
            };
        });
}

export function refreshToken(refreshToken) {
    return fpost_anonymous('/token/refresh/', {refresh: refreshToken})
        .then(async (response) => {
            return {
                status_code: response.status,
                response_data: await response.json(),
            };
        });
}

export function getService(serviceId) {
    return fget(`/services/${serviceId}/`);
}

export async function getToken(credentials) {
    return fpost_anonymous(`/service-providers/token_by_social_login/`, {'credentials': credentials})
        .then((response) => response.json());
}

export async function signUpServiceProvider(credentials, additionalData = null) {
    const payload = { credentials };
    
    // If additional data (like business details) is provided, include it
    if (additionalData) {
        Object.assign(payload, additionalData);
    }
    
    return fpost_anonymous(`/service-providers/signup/`, payload);
}

export function upsertWalkInTransaction(payload, transactionId = null) {
    if (transactionId) {
        return fput(`/carwash/walk-in/${transactionId}/`, payload);
    }

    return fpost('/carwash/walk-in/', payload);
}

export function getReservationTransaction(transactionId) {
    return fget(`/carwash/reservations/${transactionId}/`)
        .then(response => response.json());
}

export function updateReservationTransaction(payload, transactionId = null) {
    return fput(`/carwash/reservations/${transactionId}/`, payload);
}

export function getCarwashQueueStatus(serviceId) {
    return fget(`/carwash/reservations/queue_status/?service_id=${serviceId}`)
        .then(response => response.json());
}

export function getWalkInTransaction(transactionId) {
    return fget(`/carwash/walk-in/${transactionId}/`)
        .then(response => response.json());
}

export function processTransaction(transaction, payload) {
    const targetKind = transaction.kind === 'r' ? 'reservations' : 'walk-in';

    return fpatch(`/carwash/${targetKind}/${transaction.id}/process/`, payload);
}

export function completeTransaction(transaction) {
    const targetKind = transaction.kind === 'r' ? 'reservations' : 'walk-in';

    return fpatch(`/carwash/${targetKind}/${transaction.id}/complete/`);
}

export function payTransaction(transaction, payload) {
    const targetKind = transaction.kind === 'r' ? 'reservations' : 'walk-in';

    return fpatch(`/carwash/${targetKind}/${transaction.id}/pay/`, payload)
        .then(response => response.json());
}

export function cancelTransaction(transaction) {
    const targetKind = transaction.kind === 'r' ? 'reservations' : 'walk-in';

    return fdelete(`/carwash/${targetKind}/${transaction.id}/cancel/`);
}

export function getServiceMemberships(serviceProviderId) {
    return fget(`/service-providers/${serviceProviderId}/membership/`);
}

export function getCarwashServiceOffers(serviceId, options = {}) {
    let url = `/carwash/service-offers/?service_id=${serviceId}`;
    if ('query' in options && options['query']) {
        url += `&query_name=${options['query']}`;
    }
    if ('include_deactivated' in options && options['include_deactivated']) {
        url += `&include_deactivated=true`;
    }

    return fget(url)
        .then(response => response.json())
        .then(response => {
            return response.results;
        });
}

export function createCarwashServiceOffer(payload) {
    return fpost('/carwash/service-offers/', payload);
}

export function updateCarwashServiceOffer(serviceOfferId, payload) {
    return fpatch(`/carwash/service-offers/${serviceOfferId}/`, payload);
}

export function deleteCarwashServiceOffer(serviceOfferId) {
    return fdelete(`/carwash/service-offers/${serviceOfferId}/`);
}

export function getCarwashVehicleTypeServiceOffers(params = []) {
    params.push('vehicle_type_offers=yes');
    
    const hasServiceId = params.some(param => param.startsWith('service_id='));
    if (!hasServiceId) {
        throw new Error('service_id is required');
    }

    return fget(`/carwash/service-offers/?` + params.join("&")); 
}

export function updateCarwashVehicleTypeServiceOffers(payload) {
    return fpost(`/carwash/vehicle-service-offers/`, payload)
}

export function getServiceEmployees(serviceId, options = {}) {
    let url = `/employees/?service_id=${serviceId}`;
    if ('include_deactivated' in options && options['include_deactivated']) {
        url += `&include_deactivated=true`;
    }

    return fget(url)
        .then(response => response.json())
        .then(response => {
            return response.employees;
        });
}

export function createEmployee(payload) {
    return fpost(`/employees/`, payload)
}

export function updateEmployee(employeeId, payload) {
    return fpatch(`/employees/${employeeId}/`, payload)
        .then(response => response.json());
}

export function deleteEmployee(employeeId) {
    return fdelete(`/employees/${employeeId}/`);
}

export function getCarwashReportData(serviceId, startDate, endDate, otherFilters = {}, cursor = null, results = []) {
    const params = [`service_id=${serviceId}`, `start_date=${startDate}`, `end_date=${endDate}`];
    
    if (otherFilters.status) {
        params.push(`status=${otherFilters.status}`);
    }
    if (otherFilters?.workerIds?.length) {
        params.push(`worker_ids=${otherFilters.workerIds.join(',')}`);
    }
    if (cursor) {
        params.push(`cursor=${cursor}`);
    }

    return fget('/reports/carwash/?' + params.join('&'))
        .then(response => response.json())
        .then((data) => {
            const pagedResults = data.results ? [...results, ...data.results] : results;
            if (data.next) {
                const url = new URL(data.next);
                for (const [key, value] of url.searchParams) {
                    if (key === 'cursor') {
                        return getCarwashReportData(serviceId, startDate, endDate, otherFilters, value, pagedResults);
                    }
                }
            }

            return pagedResults;
        });
}

export function getCarwashReportDataExport(serviceId, startDate, endDate, otherFilters = {}) {
    const params = [`service_id=${serviceId}`, `start_date=${startDate}`, `end_date=${endDate}`];
    
    if (otherFilters.status) {
        params.push(`status=${otherFilters.status}`);
    }
    if (otherFilters?.workerIds?.length) {
        params.push(`worker_ids=${otherFilters.workerIds.join(',')}`);
    }

    return fget('/reports/carwash/export/?' + params.join('&'));
}

export function getDailySummary(params) {
    return fget('/reports/carwash/daily/?' + params.join("&"));
}

export function getOperationalEfficiency(params) {
    return fget('/reports/carwash/efficiency/?' + params.join("&"));
}

export function getCarwashOnlineStatus(serviceId) {
    return fget(`/carwash/manage/online-status/${serviceId}/`)
        .then(response => response.json())
        .then(data => data)
}

export function updateCarwashOnlineStatus(payload) {
    return fpost('/carwash/manage/online-status/', payload)
        .then(response => response.json())
        .then(data => data);
}

export function getVehicleTypes(params) {
    return fget('/vehicle-types/?' + params.join("&"));
}

export function updateCarwashLocation(serviceId, payload) {
    return fpatch(`/update-services/${serviceId}/update-location/`, payload);
}